import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

export default function PreviousExamsSection({ token, setError }) {
  const navigate = useNavigate();
  const [examTypes, setExamTypes] = useState([]);
  const [examCycles, setExamCycles] = useState([]);
  const [examTests, setExamTests] = useState([]);
  const [expandedTypes, setExpandedTypes] = useState({});
  const [expandedCycles, setExpandedCycles] = useState({});

  const getCyclesForType = (typeId) => examCycles.filter(c => c.exam_type_id === typeId);
  const getTestsForCycle = (cycleId) => examTests.filter(t => t.exam_cycle_id === cycleId);

  useEffect(() => {
    if (!token) return;

    const fetchAll = async () => {
      try {
        const [typesRes, cyclesRes, testsRes] = await Promise.all([
          api.get('/admin/exam-types', { headers: { Authorization: `Bearer ${token}` } }),
          api.get('/admin/exam-cycles', { headers: { Authorization: `Bearer ${token}` } }),
          api.get('/admin/tests', { headers: { Authorization: `Bearer ${token}` } }),
        ]);

        setExamTypes(typesRes.data.exam_types || []);
        setExamCycles(cyclesRes.data.exam_cycles || []);
        setExamTests(testsRes.data.tests || []);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load previous exams');
      }
    };

    fetchAll();
  }, [token]);

  const toggleType = (typeId) => setExpandedTypes(prev => ({ ...prev, [typeId]: !prev[typeId] }));
  const toggleCycle = (cycleId) => setExpandedCycles(prev => ({ ...prev, [cycleId]: !prev[cycleId] }));

  return (
    <div className="mb-6">
      <h3 className="text-xl font-semibold mb-4 text-indigo-600">Previous Year Exam</h3>
      <div className="space-y-3">
        {examTypes.length === 0 && (
          <div className="text-sm text-gray-600">No exams found. Please add exam types/cycles/tests via the Exams page.</div>
        )}

        {examTypes.map(type => {
          const cycles = getCyclesForType(type.id);
          return (
            <div key={type.id} className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100"
                onClick={() => toggleType(type.id)}
              >
                <span className="font-medium">{type.name}</span>
                <span className="text-xs">{expandedTypes[type.id] ? '▲' : '▼'}</span>
              </button>

              {expandedTypes[type.id] && (
                <div className="bg-white border-t border-gray-200">
                  {cycles.map(cycle => {
                    const tests = getTestsForCycle(cycle.id);
                    return (
                      <div key={cycle.id} className="border-b border-gray-100">
                        <button
                          className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50"
                          onClick={() => toggleCycle(cycle.id)}
                        >
                          <span>
                            {cycle.name} {cycle.year ? `(${cycle.year})` : ''}
                          </span>
                          <span className="text-xs">{expandedCycles[cycle.id] ? '▲' : '▼'}</span>
                        </button>
                        {expandedCycles[cycle.id] && (
                          <div className="bg-gray-50 px-6 py-2">
                            {tests.length === 0 ? (
                              <div className="text-sm text-gray-500">No tests available</div>
                            ) : (
                              <ul className="space-y-1">
                                {tests.map(test => (
                                  <li key={test.id} className="text-sm text-gray-700 flex justify-between items-center">
                                    <button
                                      type="button"
                                      className="text-left w-full"
                                      onClick={() => navigate('/questions/test/' + test.id)}
                                    >
                                      {test.title}
                                    </button>
                                    <button
                                      type="button"
                                      className="ml-3 text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200"
                                      onClick={() => navigate('/questions/test/' + test.id)}
                                    >
                                      Questions
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
